'use client';

import React from 'react';
import { ExternalLink, Globe, LayoutGrid, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TournamentSocialLinks } from '../interfaces/tournaments';
import { hasTournamentSocialLinks } from '../interfaces/tournaments';
import { cn } from '@/lib/utils';

interface TournamentSocialLinksDisplayProps {
  links?: TournamentSocialLinks | null;
  className?: string;
  compact?: boolean;
}

const LINK_CONFIG = [
  {
    key: 'discord' as const,
    label: 'Discord',
    icon: ({ className }: { className?: string }) => (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 12.3 12.3 0 0 0-.608 1.25 18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    ),
  },
  {
    key: 'stream' as const,
    label: 'Transmisión',
    icon: Radio,
  },
  {
    key: 'website' as const,
    label: 'Sitio web',
    icon: Globe,
  },
  {
    key: 'bracket_link' as const,
    label: 'Bracket externo',
    icon: LayoutGrid,
  },
] as const;

export const TournamentSocialLinksDisplay: React.FC<TournamentSocialLinksDisplayProps> = ({
  links,
  className,
  compact = false,
}) => {
  if (!hasTournamentSocialLinks(links)) return null;

  return (
    <div className={cn('space-y-3', className)}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        Enlaces
      </p>
      <div className={cn('flex flex-wrap gap-2', compact && 'flex-col')}>
        {LINK_CONFIG.map(({ key, label, icon: Icon }) => {
          const href = links?.[key];
          if (!href) return null;
          return (
            <Button key={key} variant="outline" size="sm" className="justify-start gap-2" asChild>
              <a href={href} target="_blank" rel="noopener noreferrer">
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
                <ExternalLink className="h-3.5 w-3.5 ml-auto opacity-60" />
              </a>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default TournamentSocialLinksDisplay;
